import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'react-bootstrap';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import { putRequest } from '../../../requestUtils';
import '../ProjectDetails.scss';
import DropdownIndicator from '../../common/DropdownIndicator';

const formStep = 'admin';

const customStyles = {
  option: (styles, { isDisabled }) => ({
    ...styles,
    opacity: isDisabled ? 0.5 : 1,
    cursor: isDisabled ? 'not-allowed' : 'default',
  }),
};

const processTakeRate = (takeRate) => {
  if (!takeRate) return 25.0;
  return Math.round(takeRate * 10000) / 100;
};

class AdminModal extends Component {
  constructor(props) {
    super(props);

    this.state = {
      startDate: null,
      consultantNeedsToSignAgreement: false,
      taxType: '',
      consultantWorkingWith: '',
      internalBudgetApproval: false,
      jobTitle: '',
      takeRate: null,
      projectType: null,
      showModal: props.modalOpen,
    };

    this.handleShowModal = this.handleShowModal.bind(this);
    this.handleHideModal = this.handleHideModal.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.updateProjectDetails = this.updateProjectDetails.bind(this);
  }

  componentDidMount() {
    const { projectResourceId } = this.props;

    if (!projectResourceId) return;

    this.updateProjectDetails();
  }

  updateProjectDetails() {
    const { getProjectDetails } = this.props;

    getProjectDetails(formStep).then((response) => {
      if (response.data.approximate_start_date_local) {
        this.setState({ startDate: new Date(`${response.data.approximate_start_date_local} 0:00`) });
      }
      this.setState({
        consultantNeedsToSignAgreement: response.data.consultant_needs_sign_agreement,
        taxType: response.data.tax_type,
        consultantWorkingWith: response.data.consultant_working_with,
        internalBudgetApproval: response.data.have_internal_budget_approval,
        jobTitle: response.data.job_title,
        takeRate: processTakeRate(response.data.clora_take_rate),
        projectType: response.data.project_type_for_select,
      });
    });
  }

  handleHideModal() {
    this.setState({ showModal: false });
  }

  handleShowModal() {
    this.setState({ showModal: true });
  }

  handleSubmit() {
    const { csrfToken, projectResourceId, afterSubmit } = this.props;
    const {
      startDate, consultantNeedsToSignAgreement, taxType, consultantWorkingWith,
      internalBudgetApproval, jobTitle, takeRate, projectType,
    } = this.state;

    const params = {
      project: {
        approximate_start_date_local: startDate,
        consultant_needs_sign_agreement: consultantNeedsToSignAgreement,
        tax_type: taxType,
        consultant_working_with: consultantWorkingWith,
        have_internal_budget_approval: internalBudgetApproval,
        project_type: projectType.value,
        clora_take_rate: takeRate,
        job_title: jobTitle,
      },
    };

    putRequest(
      `/projects/${projectResourceId}`,
      params,
      csrfToken,
    ).then(() => {
      this.handleHideModal();
      afterSubmit();
    }).catch((error) => {
      this.setState({ errors: error.name });
      return null;
    });
  }

  renderProjectType() {
    const { projectTypes } = this.props;

    return (
      <div className="admin-project-type mt-4">
        <div className="body-text-2 demibold py-3">
          Project type
        </div>
        <Select
          components={{ DropdownIndicator, IndicatorSeparator: () => null }}
          classNamePrefix="clora-multiselect"
          closeMenuOnSelect
          value={this.state.projectType}
          className="project-type-select"
          id="project-type-select"
          onChange={(option) => this.setState({ projectType: option })}
          isDisabled={false}
          placeholder="Select the project type"
          options={projectTypes}
          isOptionDisabled={(option) => option.label.toLowerCase() === 'request'}
          styles={customStyles}
        />
      </div>
    );
  }

  renderTakeRate() {
    return (
      <div className="mt-3">
        <div className="body-text-2 demibold mt-4 mb-2">
          Clora take rate
        </div>
        <input
          name="clora-take-rate"
          className="form-control rate-form"
          value={this.state.takeRate || ''}
          onChange={(e) => this.setState({ takeRate: e.target.value })}
        />
        &nbsp;&nbsp;%
      </div>
    );
  }

  renderStartDate() {
    const today = new Date();
    const { projectResourceId } = this.props;
    return (
      <div className="body-text-2 mt-4">
        <div className="demibold mb-2">
          Project start date
        </div>
        <div className="start-date-datepicker-container">
          <label className="g-row--always" htmlFor={`start-date-${projectResourceId}`}>
            <DatePicker
              selected={this.state.startDate}
              onChange={(newDate) => this.setState({ startDate: newDate })}
              dateFormat="MM/dd/yyyy"
              className="start-date-datepicker"
              id={`start-date-${projectResourceId}`}
              minDate={today}
              openToDate={today}
            />
            <span className="input-group-addon">
              <i className="fas fa-calendar adjust-center" />
            </span>
          </label>
        </div>
      </div>
    );
  }

  renderRadioChoice(attribute, labelText) {
    const id = `${attribute}-${labelText}`;
    let choice;
    if (labelText === 'Yes') {
      choice = true;
    } else if (labelText === 'No') {
      choice = false;
    } else {
      choice = labelText;
    }

    return (
      <div className={`${id}-choice choice`}>
        <input
          type="radio"
          id={id}
          name={id}
          onChange={() => this.setState({ [attribute]: choice })}
          checked={this.state[attribute] === choice}
        />
        <label htmlFor={id}>
          &nbsp;
          {labelText}
        </label>
      </div>
    );
  }

  renderMultipleChoice() {
    return (
      <div className="body-text-2 mt-3">
        <div className="demibold py-3">
          Non-compete agreement required?
        </div>
        {this.renderRadioChoice('consultantNeedsToSignAgreement', 'Yes')}
        {this.renderRadioChoice('consultantNeedsToSignAgreement', 'No')}
        <div className="demibold py-3 mt-2">
          Tax filing
        </div>
        {this.renderRadioChoice('taxType', '1099')}
        {this.renderRadioChoice('taxType', 'W-2')}
        <div className="demibold py-3 mt-2">
          Internal budget approved?
        </div>
        {this.renderRadioChoice('internalBudgetApproval', 'Yes')}
        {this.renderRadioChoice('internalBudgetApproval', 'No')}
      </div>
    );
  }

  renderTextAreas() {
    return (
      <div className="body-text-2 mt-3">
        <div className="demibold py-3">
          Who will the candidate be working with?
        </div>
        <textarea
          name="consultant-working-with"
          className="scope-form form-control fs-hide null"
          value={this.state.consultantWorkingWith || ''}
          onChange={(e) => this.setState({ consultantWorkingWith: e.target.value })}
        />
        <div className="mt-3">
          <div className="demibold py-3">
            Job title
          </div>
          <textarea
            name="job-title"
            className="scope-form form-control fs-hide null"
            value={this.state.jobTitle || ''}
            onChange={(e) => this.setState({ jobTitle: e.target.value })}
          />
        </div>
      </div>
    );
  }

  renderButtons() {
    return (
      <div className="buttons-container g-row mt-4">
        <div className="g-column g-flex-6" />
        <div className="g-column g-flex-3">
          <button className="btn btn-secondary button-back" onClick={this.handleHideModal}>
            Cancel
          </button>
        </div>
        <div className="g-column g-flex-3">
          <button className="btn btn-primary button-submit" onClick={this.handleSubmit}>
            Save
          </button>
        </div>
      </div>
    );
  }

  renderErrors() {
    const { errors } = this.state;

    return (errors
      && (
      <div id="error_explanation">
        <h3>Errors prohibited this project from being saved:</h3>
        <ul>
          {errors.map((e) => (
            <li>{e}</li>
          ))}
        </ul>
      </div>
      )
    );
  }

  renderModalLink() {
    return (
      <a
        role="link"
        tabIndex={0}
        className="project-edit-link ml"
        onClick={this.handleShowModal}
      >
        <i className="fa fa-edit" />
&nbsp;
        {(this.props.formType === 'modal-link') ? 'Edit' : ''}
      </a>
    );
  }

  renderContent() {
    return (
      <div className="style-guide project-admin-container">
        <div className="admin-entry-container">
          {this.renderProjectType()}
          {this.renderTakeRate()}
          {this.renderStartDate()}
          {this.renderMultipleChoice()}
          {this.renderTextAreas()}
          {this.renderButtons()}
        </div>
      </div>
    );
  }

  renderModal() {
    return (
      <Modal
        show={this.state.showModal}
        onHide={this.handleHideModal}
        dialogClassName="admin-modal"
      >
        <Modal.Header closeButton className="admin-modal-header">
          <div className="ms-5 mt-4 mb-3">Admin</div>
        </Modal.Header>
        <Modal.Body className="admin-modal-body">
          {this.renderContent()}
        </Modal.Body>
      </Modal>
    );
  }

  render() {
    if (this.props.formType === 'page') {
      return this.renderContent();
    }
    return (
      <span>
        { this.renderModalLink() }
        { this.renderModal() }
      </span>
    );
  }
}

AdminModal.propTypes = {
  formType: PropTypes.string, // one of 'page', 'modal-link', 'modal-icon'
  projectResourceId: PropTypes.string,
  csrfToken: PropTypes.string.isRequired,
  afterSubmit: PropTypes.func,
  getProjectDetails: PropTypes.func.isRequired,
  projectTypes: PropTypes.shape({}),
  modalOpen: PropTypes.bool,
};

AdminModal.defaultProps = {
  formType: 'page',
  projectResourceId: null,
  afterSubmit: () => {},
  projectTypes: {},
  modalOpen: false,
};

export default AdminModal;
